/**
 * AmPmToggle
 *
 * Scroll-based AM/PM selector matching the TimeScrollColumn visual style.
 * Snaps between two items with fade overlays and highlight.
 */

import { useCallback, useEffect, useRef } from 'react';
import {
  NativeScrollEvent,
  NativeSyntheticEvent,
  StyleSheet,
  Text,
  View,
} from 'react-native';
import Animated from 'react-native-reanimated';

import { BorderRadius, Spacing } from '@/constants/spacing';
import { getBodyFont } from '@/constants/typography';

const ITEM_HEIGHT = 44;
const VISIBLE_COUNT = 5;
const COLUMN_HEIGHT = ITEM_HEIGHT * VISIBLE_COUNT;
const CONTENT_PADDING = ITEM_HEIGHT * 2;

const PERIODS = ['AM', 'PM'] as const;

type AmPmToggleProps = {
  isAm: boolean;
  onChange: (isAm: boolean) => void;
  accentColor: string;
  textColor: string;
  textMutedColor: string;
  cardBackgroundColor: string;
};

export function AmPmToggle({
  isAm,
  onChange,
  accentColor,
  textColor,
  cardBackgroundColor,
}: AmPmToggleProps) {
  const scrollViewRef = useRef<Animated.ScrollView>(null);
  const isUserScrolling = useRef(false);
  const selectedIndex = isAm ? 0 : 1;

  useEffect(() => {
    if (!isUserScrolling.current) {
      requestAnimationFrame(() => {
        scrollViewRef.current?.scrollTo({
          y: selectedIndex * ITEM_HEIGHT,
          animated: false,
        });
      });
    }
  }, [selectedIndex]);

  const handleMomentumScrollEnd = useCallback(
    (event: NativeSyntheticEvent<NativeScrollEvent>) => {
      isUserScrolling.current = false;
      const offsetY = event.nativeEvent.contentOffset.y;
      const index = Math.round(offsetY / ITEM_HEIGHT);
      const clampedIndex = Math.max(0, Math.min(index, 1));
      const newIsAm = clampedIndex === 0;

      if (newIsAm !== isAm) {
        onChange(newIsAm);
      }
    },
    [isAm, onChange],
  );

  const handleScrollBeginDrag = useCallback(() => {
    isUserScrolling.current = true;
  }, []);

  return (
    <View style={styles.container}>
      <View style={styles.scrollWrapper}>
        {/* Top fade overlay */}
        <View
          style={[styles.fadeOverlayTop, { backgroundColor: cardBackgroundColor }]}
          pointerEvents="none"
        />

        {/* Selected row highlight */}
        <View
          style={[
            styles.selectedHighlight,
            { backgroundColor: accentColor + '18', borderColor: accentColor + '40' },
          ]}
          pointerEvents="none"
        />

        <Animated.ScrollView
          ref={scrollViewRef}
          showsVerticalScrollIndicator={false}
          snapToInterval={ITEM_HEIGHT}
          decelerationRate="fast"
          contentContainerStyle={{ paddingVertical: CONTENT_PADDING }}
          onMomentumScrollEnd={handleMomentumScrollEnd}
          onScrollBeginDrag={handleScrollBeginDrag}
          style={styles.scrollView}
          nestedScrollEnabled
        >
          {PERIODS.map((period, index) => {
            const isSelected = index === selectedIndex;
            return (
              <View key={period} style={styles.itemContainer}>
                <Text
                  style={[
                    styles.itemText,
                    {
                      fontFamily: getBodyFont(),
                      color: isSelected ? accentColor : textColor,
                      fontWeight: isSelected ? '700' : '400',
                      fontSize: isSelected ? 18 : 14,
                    },
                  ]}
                >
                  {period}
                </Text>
              </View>
            );
          })}
        </Animated.ScrollView>

        {/* Bottom fade overlay */}
        <View
          style={[styles.fadeOverlayBottom, { backgroundColor: cardBackgroundColor }]}
          pointerEvents="none"
        />
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    width: 52,
    height: COLUMN_HEIGHT,
    position: 'relative',
    marginLeft: Spacing.sm,
  },
  scrollWrapper: {
    width: '100%',
    height: '100%',
    overflow: 'hidden',
  },
  scrollView: {
    height: COLUMN_HEIGHT,
  },
  itemContainer: {
    height: ITEM_HEIGHT,
    justifyContent: 'center',
    alignItems: 'center',
  },
  itemText: {
    textAlign: 'center',
    letterSpacing: 0.5,
  },
  selectedHighlight: {
    position: 'absolute',
    top: CONTENT_PADDING,
    left: Spacing.xs,
    right: Spacing.xs,
    height: ITEM_HEIGHT,
    borderRadius: BorderRadius.sm,
    borderWidth: 1,
    zIndex: 1,
  },
  fadeOverlayTop: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: ITEM_HEIGHT * 1.5,
    zIndex: 2,
    opacity: 0.7,
  },
  fadeOverlayBottom: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    height: ITEM_HEIGHT * 1.5,
    zIndex: 2,
    opacity: 0.7,
  },
});
